import { getEvmClient } from '@1delta/providers'
import type { Address } from 'viem'
import { YieldFetcher } from '../../types'
import { annualizeRateDeltaPercent } from '../annualizeRate'

/**
 * Infrared iBERA / iBGT — intrinsic APR.
 *
 * Source is Infrared's public APR-history API. Both routes return
 * `[{ timestamp, value }]` where `value` is an APR fraction (e.g.
 * `0.036` = 3.6%); we take the latest point and convert to percent.
 *
 *  - iBERA:  /api/ibera/apr-history?chainId=80094
 *  - iBGT:   /api/backend-vaults/<staking-vault>/apr-history?chainId=80094
 *
 * iBERA additionally falls back to the on-chain exchange-rate growth
 * if the API is unreachable: iBERA is non-rebasing and value-accruing,
 * so the APR is recovered from the growth of
 * `iBERA.convertToAssets(1e18)` over a fixed lookback window,
 * annualised:
 *
 *   apr = (rateNow / rateThen - 1) * (yearSeconds / elapsedSeconds)
 *
 * That fallback needs an archival Berachain RPC; pruned public nodes
 * return `0` for old state, which the sanity check rejects. Mirrors
 * the stCELO rate-delta fetcher.
 *
 * iBGT has no on-chain rate getter (1:1 BGT wrapper; plain ERC-20),
 * so there is no equivalent fallback — if the API is down, the
 * fetcher surfaces `0`.
 */

const CHAIN_ID = '80094'
const IBERA: Address = '0x9b6761bf2397bb5a6624a856cc84a3a14dcd3fe5'

const ONE_E18 = 10n ** 18n

/** Lookback window for the rate-delta. iBERA accrues via periodic
 *  reward sweeps, so a multi-day window is needed for a stable figure. */
const WINDOW_SECONDS = 3 * 24 * 60 * 60
/** Berachain block time is ~2s; used only to estimate the historical
 *  block. The actual elapsed time is read back from block timestamps. */
const BLOCK_TIME_SECONDS = 2
const WINDOW_BLOCKS = BigInt(Math.floor(WINDOW_SECONDS / BLOCK_TIME_SECONDS))

const MAX_RPC_TRIES = 5

const CONVERT_TO_ASSETS_ABI = [
  {
    name: 'convertToAssets',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'uint256', name: 'shares' }],
    outputs: [{ type: 'uint256' }],
  },
] as const

const readRateAt = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  client: any,
  blockNumber?: bigint,
): Promise<bigint> =>
  client.readContract({
    address: IBERA,
    abi: CONVERT_TO_ASSETS_ABI,
    functionName: 'convertToAssets',
    args: [ONE_E18],
    ...(blockNumber !== undefined ? { blockNumber } : {}),
  }) as Promise<bigint>

const computeApr = async (): Promise<number> => {
  let lastErr: unknown
  for (let rpcId = 0; rpcId < MAX_RPC_TRIES; rpcId++) {
    try {
      const client = getEvmClient(CHAIN_ID, rpcId)
      const head = await client.getBlockNumber()
      const pastBlock = head > WINDOW_BLOCKS ? head - WINDOW_BLOCKS : 0n

      const [rateNow, rateThen, headBlock, thenBlock] = await Promise.all([
        readRateAt(client),
        readRateAt(client, pastBlock),
        client.getBlock({ blockNumber: head }),
        client.getBlock({ blockNumber: pastBlock }),
      ])

      // Archive sanity: both rates must be real (≥ 1e18) and the rate
      // must be non-decreasing. Pruned nodes return 0 here.
      if (rateNow < ONE_E18 || rateThen < ONE_E18 || rateNow < rateThen) {
        throw new Error(
          `non-archival or invalid rates (now=${rateNow}, then=${rateThen})`,
        )
      }

      const elapsed = Number(headBlock.timestamp - thenBlock.timestamp)
      if (elapsed <= 0) throw new Error('non-positive elapsed window')

      const apr = annualizeRateDeltaPercent(rateNow, rateThen, elapsed)
      if (!Number.isFinite(apr) || apr < 0) throw new Error(`bad apr ${apr}`)
      return apr
    } catch (e) {
      lastErr = e
    }
  }
  throw new Error(
    `iBERA APR: no archival Berachain RPC returned valid historical state (${String(lastErr)})`,
  )
}

/** Infrared APR-history routes. Both return `[{ timestamp, value }]` where
 *  `value` is an APR fraction (e.g. 0.04 = 4%). iBERA has a token slug
 *  route; iBGT is keyed by its staking-vault address under `backend-vaults`. */
const IBERA_APR_URL =
  'https://infrared.finance/api/ibera/apr-history?chainId=80094'
const IBGT_VAULT = '0x75f3be06b02e235f6d0e7ef2d462b29739168301'
const IBGT_APR_URL = `https://infrared.finance/api/backend-vaults/${IBGT_VAULT}/apr-history?chainId=80094`

/** Fetch the latest APR (as a percent) from an Infrared apr-history URL.
 *  Throws on HTTP error, non-JSON (SPA HTML), or an empty/invalid series. */
const fetchInfraredAprPercent = async (url: string): Promise<number> => {
  const res = await fetch(url, {
    headers: { accept: 'application/json' },
    signal: AbortSignal.timeout(8000),
  })
  if (!res.ok) throw new Error(`Infrared APR HTTP ${res.status}`)
  const ct = res.headers.get('content-type') ?? ''
  if (!ct.includes('application/json')) {
    throw new Error('Infrared APR: non-JSON response')
  }
  const series = (await res.json()) as Array<{ value?: unknown }>
  const last = Array.isArray(series)
    ? series[series.length - 1]?.value
    : undefined
  if (typeof last !== 'number' || !Number.isFinite(last)) {
    throw new Error('Infrared APR: no valid APR point')
  }
  return last * 100
}

export const iberaFetcher: YieldFetcher = {
  label: 'IBERA',
  fetch: async () => {
    try {
      return { iBERA: await fetchInfraredAprPercent(IBERA_APR_URL) }
    } catch {
      // API unreachable — fall back to on-chain rate-delta.
      return { iBERA: await computeApr() }
    }
  },
}

export const ibgtFetcher: YieldFetcher = {
  label: 'IBGT',
  fetch: async () => {
    return { iBGT: await fetchInfraredAprPercent(IBGT_APR_URL) }
  },
}
